import { arrayOf, func, number, object } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Label, Row } from 'reactstrap';

import {
  collectionIdPrefix,
  contentIdPrefix,
} from '../../../campaign-editor/constants';
import TagInput from '../../../generic/TagInput';
import api from '../../../REST/api';
import withApiRequest from '../../../REST/with-api-request';
import { flash, tapFlashError } from '../../../utils/flash';
import ContentEntryNameSearch from './ContentEntryNameSearch';

const endpoint = api.getClmContentPreview;

function ContentIds({
  onRequest,
  onSuccess,
  campaignLineProducts,
  campaignId,
}) {
  const [contentIds, setContentIds] = useState([]);
  const [chosenFilters, setChosenFilters] = useState(
    campaignLineProducts.map(product => product?.code),
  );
  const [contentEntryNames, setContentEntryNames] = useState([]);

  const clearContentEntryNames = () => {
    setContentEntryNames([]);
  };

  const isValidContentId = ids => {
    const validIds = ids
      .map(contentId => {
        if (collectionIdPrefix.test(contentId)) {
          tapFlashError()(
            new Error(
              `The input ${contentId} is a collection ID. It should be added in the collection IDs field and not in the content IDs field.`,
            ),
          );
          return false;
        }
        if (!contentIdPrefix.test(contentId)) {
          tapFlashError()(
            new Error(
              `The input ${contentId} should contain a valid content prefix.`,
            ),
          );
          return false;
        }
        return true;
      })
      .some(value => value === false);
    return !validIds;
  };

  const handleChange = e => {
    if (isValidContentId(e.target.value)) {
      setContentIds(e.target.value);
    }
  };

  useEffect(() => {
    if (contentIds?.length > 0) {
      onRequest({ data: { contentIds, campaignId } })
        .then(files => {
          onSuccess(files);
          const returnedIds = Object.keys(files?.clmContentPreview || {});
          if (returnedIds.length === 0) {
            flash(
              "No matching creatives found for the entered content ID(s). Check that the IDs exist in the correct environment and that their content types support this campaign's product, offer type, and proposition.",
              'warning',
            );
          } else {
            const missingIds = contentIds.filter(
              id => !returnedIds.includes(id),
            );
            if (missingIds.length > 0) {
              flash(
                `Some content IDs could not be added: ${missingIds.join(
                  ', ',
                )}. Check their availability and compatibility with this campaign.`,
                'warning',
              );
            } else {
              flash('Contentful Creatives uploaded successfully');
            }
          }
        })
        .catch(console.error); // eslint-disable-line no-console
    }
  }, [contentIds, onRequest, onSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Row className="ml-3 mt-5 mb-5 align-items-start">
      <Col sm={5} className="align-self-start">
        <Label for="contentEntryNameSearch" check>
          <span className="mb-2 d-inline-block">
            Search for content entries using keywords
          </span>
        </Label>
        <ContentEntryNameSearch
          chosenFilters={chosenFilters}
          contentEntryNames={contentEntryNames}
          setChosenFilters={setChosenFilters}
          setContentEntryNames={setContentEntryNames}
          campaignLineProducts={campaignLineProducts}
          onClearSelected={clearContentEntryNames}
          onSuccess={onSuccess}
          type="contentId"
          minSearchLength={4}
          campaignId={campaignId}
        />
      </Col>
      <Col sm={1} className="d-flex justify-content-center align-items-center">
        <span className="fw-bold fs-1 mt-4 d-inline-block">OR</span>
      </Col>
      <Col sm={5} className="align-self-start">
        <Label for="ca-contentIds" check>
          <span className="mb-2 d-inline-block">Insert content entry Ids</span>
        </Label>
        <TagInput
          id="ca-contentIds"
          name="contentIds"
          value={contentIds}
          onChange={handleChange}
          allowCopy
        />
      </Col>
    </Row>
  );
}

ContentIds.propTypes = {
  onRequest: func.isRequired,
  onSuccess: func.isRequired,
  campaignLineProducts: arrayOf(object), // eslint-disable-line react/forbid-prop-types
  campaignId: number.isRequired,
};

ContentIds.defaultProps = {
  campaignLineProducts: [],
};

export default withApiRequest({
  query: endpoint,
})(ContentIds);
